import {
  useGetAdvertisementByIdQuery,
  useUpdateAdvertisementMutation,
} from "@/app/features/advertisement/advertisementApiSlice";
import { useGetPopularSearchCategoriesQuery } from "@/app/features/popularSearch/popularSearchApiSlice";
import CreatableAutocomplete from "@/components/forms/FormAutoComplete";
import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import MultiSelectField from "@/components/forms/FormMultiSelect";
import FormSelect from "@/components/forms/FormSelect";
import FormSwitch from "@/components/forms/FormSwitch";
import bannerTypes, {
  bannerPostions,
  bannerType,
  frontendPages,
  pageLists,
} from "@/constants/bannertype";
import states from "@/constants/states";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik, useFormikContext } from "formik";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  adName: Yup.string()
    .required()
    .min(2)
    .max(50)
    .label("Advertisement Title")
    .trim(),
  webLink: Yup.string().required().min(5).label("Advertisement Detail").trim(),
  adType: Yup.string().required().min(1).label("Advertisement Type").trim(),
  bannerPosition: Yup.string().label("Banner Position").required(),
  pageName: Yup.string()
    .label("Page Name")
    .test("test", "Page Name is required", function (value) {
      if (this.parent.adType && this.parent.adType === bannerType.TargetedAD) {
        return value ? true : false;
      }
      return true;
    }),

  state: Yup.string().required().min(1).label("Advertisement State").trim(),
  status: Yup.boolean().required().label("Advertisement Status"),
  desktopBanner: Yup.mixed()
    .optional()
    .test(
      "type",
      "Only the following formats are accepted: .jpg, .webp and.png",
      (value) => {
        if (!value) {
          return true;
        }
        return (
          value &&
          (value.type === "image/png" ||
            value.type === "image/jpg" ||
            value.type === "image/jpeg" ||
            value.type === "image/webp")
        );
      }
    )
    .label("Desktop Banner"),
  mobileBanner: Yup.mixed()
    .optional()
    .test(
      "type",
      "Only the following formats are accepted: .jpg, .webp and.png",
      (value) => {
        if (!value) {
          return true;
        }
        return (
          value &&
          (value.type === "image/png" ||
            value.type === "image/jpg" ||
            value.type === "image/jpeg" ||
            value.type === "image/webp")
        );
      }
    )
    .label("Mobile Banner"),
  models: Yup.array()
    .of(Yup.string())
    .label("Models Name")
    .test("test", "Models is required", function (value) {
      if (this.parent.adType && this.parent.adType === bannerType.TargetedAD) {
        return value ? true : false;
      }
      return true;
    }),
});

export default function ViewAdvertisementPage() {
  const params = useParams();
  const { data, isLoading, isError, error } = useGetAdvertisementByIdQuery(
    params.id
  );
  const {
    data: popularSearchDara,
    isLoading: isPopularCategoryLoading,
    isError: isPopularCategoryError,
    error: popularCategoryError,
  } = useGetPopularSearchCategoriesQuery();

  const [updateAdvertisement, { isLoading: isUpdating }] =
    useUpdateAdvertisementMutation();

  const handleSubmit = async (inputs) => {
    try {
      console.log(inputs);
      const formData = new FormData();
      formData.append("adName", inputs.adName);
      formData.append("adType", inputs.adType);
      formData.append("bannerPosition", inputs.bannerPosition);
      formData.append("webLink", inputs.webLink);

      if (inputs.adType === bannerType.TargetedAD) {
        formData.append("pageName", inputs.pageName);
        formData.append("models", inputs.models);
      }

      formData.append("status", inputs.status);
      formData.append("state", inputs.state);

      if (inputs.desktopBanner) {
        formData.append("desktopImage", inputs.desktopBanner);
      }

      if (inputs.mobileBanner) {
        formData.append("mobileImage", inputs.mobileBanner);
      }

      const result = await updateAdvertisement({
        id: params.id,
        data: formData,
      });

      if (result && result.data) {
        toast.success(result.data.message);
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const advertisement = data?.data?.advertisement;

  if (isLoading || isPopularCategoryLoading) {
    return (
      <Skeleton
        sx={{
          transform: "scale(1, 1.0)",
          transformOrigin: "0% 0%",
        }}
        width="100%"
        height="600px"
      />
    );
  }

  if (isError || isPopularCategoryError) {
    return (
      <p>
        {error?.data?.message ||
          popularCategoryError?.data?.message ||
          "Something went wrong"}
      </p>
    );
  }

  const popularSearchCategory = popularSearchDara.data.data;

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Edit Advertisement</Typography>
          </Stack>

          <Formik
            initialValues={{
              adName: advertisement.adName || "",
              adType: advertisement.adType,
              bannerPosition: advertisement.bannerPosition,
              pageName: advertisement.pageName || "",
              webLink: advertisement.webLink || "",
              models: advertisement.models || [],
              status: advertisement.status || false,
              state: advertisement.state || "ACT",
              adDesktopBanner: advertisement.adDesktopBanner || null,
              adMobileBanner: advertisement.adMobileBanner || null,
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ setFieldValue, values, setFieldTouched, setFieldError }) => (
              <Form>
                <Stack spacing={3}>
                  <AdTypeWatcher />
                  <FormInput
                    label="Ad Name"
                    name="adName"
                    type="text"
                    placeholder="Enter Advertisement Text"
                  />
                  <FormSelect
                    name="adType"
                    label="Ad Type"
                    items={bannerTypes}
                  />
                  {values.adType === bannerType.TargetedAD && (
                    <FormSelect
                      name="pageName"
                      label="Page Name"
                      items={pageLists}
                    />
                  )}
                  <FormSelect
                    name="bannerPosition"
                    label="Banner Position"
                    items={bannerPostions}
                  />
                  {values.adType === bannerType.TargetedAD &&
                    values.pageName === frontendPages.POPULAR_SEARCH && (
                      <MultiSelectField
                        name="models"
                        label="Popular Search Category"
                        items={popularSearchCategory}
                      />
                    )}
                  {values.adType === bannerType.TargetedAD &&
                    (values.pageName === frontendPages.MODEL ||
                      values.pageName === frontendPages.BRAND) && (
                      <CreatableAutocomplete
                        name="models"
                        label="Models"
                        placeholder="Type and press enter"
                      />
                    )}

                  <FormInput label="Web Link" name="webLink" type="text" />

                  <FormImageInput
                    name="desktopBanner"
                    imageValues={values.adDesktopBanner}
                    onChangeSelectedImage={(event) => {
                      setFieldTouched(event.target.name);
                      setFieldValue(event.target.name, event.target.files[0]);
                    }}
                    onError={(message) => {
                      setFieldError("adDesktopBanner", message);
                    }}
                    onCheckRes={(checkResolution) => {
                      setFieldValue("checkResolution", checkResolution);
                    }}
                    checkResolution={values.checkResolution}
                    label="Desktop View"
                  />

                  <FormImageInput
                    name="mobileBanner"
                    imageValues={values.adMobileBanner}
                    onChangeSelectedImage={(event) => {
                      setFieldTouched(event.target.name);
                      setFieldValue(event.target.name, event.target.files[0]);
                    }}
                    onError={(message) => {
                      setFieldError("adMobileBanner", message);
                    }}
                    onCheckRes={(checkResolution) => {
                      setFieldValue("checkResolution", checkResolution);
                    }}
                    checkResolution={values.checkResolution}
                    label="Mobile View"
                  />

                  <FormSelect
                    name="state"
                    label="State"
                    items={[{ code: "All", name: "All" }, ...states]}
                  />

                  <FormSwitch label="Active Status" name="status" />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    Update
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}

function AdTypeWatcher() {
  const { values, setFieldValue } = useFormikContext();

  const prevAdTypeRef = useRef(values.adType);
  const prevPageNameRef = useRef(values.pageName);

  useEffect(() => {
    // If adType changed to Generic -> reset pageName and models
    if (
      prevAdTypeRef.current !== values.adType &&
      values.adType === "Generic"
    ) {
      setFieldValue("pageName", "");
      setFieldValue("models", []);
    }

    // If pageName changed -> reset models
    if (prevPageNameRef.current !== values.pageName) {
      setFieldValue("models", []);
    }

    // Update refs
    prevAdTypeRef.current = values.adType;
    prevPageNameRef.current = values.pageName;
  }, [values.adType, values.pageName, setFieldValue]);

  return null;
}
